import { AppContext } from "./AppContext";
import { useState, useCallback, ReactNode, FC, useRef } from "react";
import { DEFAULT_DIALOG_HANDLE, DEFAULT_DIALOG_HANDLES, DEFAULT_FORCED_RENDERS, DEFAULT_PRICE_RULES, DEFAULT_PROCESSED_TABLE_LAYOUT, DEFAULT_PRODUCTS, DEFAULT_ROUTE, DEFAULT_SCREEN, DEFAULT_SHIFTS, DEFAULT_SHORTCUTS, DEFAULT_TABLE_MAP_HANDLE, DEFAULT_VENUE_DATA, booking } from "../utils/symbols";
import { load, save, _, deleteItem } from "../utils/helpers";
import { DialogHandles, PriceRule, Shift, DialogHandle, DialogId, ProcessedTableLayout, Screen, Product, VenueData, SafeVenueData, Shortcut, ComputedShiftData} from "../types";
import { TableLayoutHandle } from "../components/table-layout/TableLayout";

interface AppProviderProps {children: ReactNode}


export const AppProvider:FC<AppProviderProps> = ({ children }) => {

    const [forcedRenders, RE_RENDER_APP] = useState<number>(DEFAULT_FORCED_RENDERS)
    const [shifts, setShifts] = useState<(Shift & ComputedShiftData)[]>(DEFAULT_SHIFTS)
    const [screen, setScreen] = useState<Screen>(DEFAULT_SCREEN)
    const [priceRules, setPriceRules] = useState<PriceRule[]>(load('localStorage','price-rules', DEFAULT_PRICE_RULES))
    const [tableMapHandle, setTableMapHandle] = useState<TableLayoutHandle>(DEFAULT_TABLE_MAP_HANDLE)
    const [dialogHandles, setDialogHandles] = useState<DialogHandles>(DEFAULT_DIALOG_HANDLES)
    const [products, setProducts] = useState<Product[]>(DEFAULT_PRODUCTS)
    const [venueData, setVenueData] = useState<VenueData>(load('localStorage','venue-data', DEFAULT_VENUE_DATA))
    const [route, setRoute] = useState<string>(load('sessionStorage','route',DEFAULT_ROUTE))
    const [shortcuts, setShortcuts] = useState<Map<string, Shortcut>>(DEFAULT_SHORTCUTS)
    const [layout, setLayout] = useState<ProcessedTableLayout>(load('localStorage','layout',DEFAULT_PROCESSED_TABLE_LAYOUT))

    const retryCount = useRef(0)

    const forceRender = useCallback(() => {
        RE_RENDER_APP(x => x + 1)
    }, [])
    
    const updateShifts = useCallback((data: (Shift & ComputedShiftData)[]) => {
        setShifts(data)
    }, [])

    const updateScreen = useCallback((data: UIEvent) => {
        setScreen({
            width: window.innerWidth,
            height: window.innerHeight
        })
    }, [])

    const updatePriceRules = useCallback((data: PriceRule[]) => {
        setPriceRules(data)
        save('localStorage','price-rules', data)
        save('localStorage','price-rules-date', Date.now())
    },[])

    const updateTableMapHandle = useCallback((data: TableLayoutHandle) => {
        setTableMapHandle(data)
    }, [])

    const addDialogHandle = useCallback((id:DialogId, data: DialogHandle) => {
        setDialogHandles((prevState) => {
            const newState = new Map(prevState.entries())
            newState.set(id, data)
            return newState
        })
    }, [])

    const updateProducts = useCallback((data: Product[]) => {
         setProducts(data)
    }, [])

    const updateVenueData = useCallback((data: SafeVenueData) => {
        setVenueData(data)
        save('localStorage','venue-data', data)
    }, [])

    const navigate = useCallback((route: string) => {
        setRoute(route)
        save('sessionStorage','route', route)
    }, [])

    const genericError = useCallback((retryFnc: () => void) => {
        const appDialog = dialogHandles.get('app-dialog') ?? DEFAULT_DIALOG_HANDLE
        appDialog.openDialog({
            dialog: 'error',
            className: 'error w-100 h-100 df aic jcc',
            message: _('0ge_anune__'),
            button: _('0ge_retry__'),
            callback: () => {
                retryCount.current += 1
                if (retryCount.current > 1) {
                    deleteItem('booking-data')
                    window.location.reload()
                    return;
                }
                appDialog.closeDialog()
                retryFnc()
            },
            contactUs: true
        })
    }, [dialogHandles])

    const addShortcuts = useCallback((data: Shortcut[]) => {
        setShortcuts(prev => {
            data.forEach(item => prev.set(item.id, item))
            return prev
        })
    }, [])

    const updateLayout = useCallback((data: ProcessedTableLayout) => {
        setLayout(data)
        save('localStorage','layout',data)
    }, [])

    if (!booking.hasRender) booking.setRender(forceRender)

    return (
        <AppContext.Provider value={{
            state: {
                booking,
                forcedRenders,
                shifts,
                screen,
                priceRules,
                tableMapHandle,
                dialogHandles,
                products,
                venueData,
                route,
                shortcuts,
                layout
            },
            actions: {
                updateShifts,
                updateScreen,
                updatePriceRules,
                updateTableMapHandle,
                forceRender,
                addDialogHandle,
                updateProducts,
                updateVenueData,
                navigate,
                genericError,
                addShortcuts,
                updateLayout
            }
        }}>
            {children}
        </AppContext.Provider>
    )
}